import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, AlertTriangle, Lock, Unlock, History } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', red: '#E24B4A', amber: '#B07407' };
const VERDICT = {
  ok: { label: 'WITHIN', bg: 'rgba(29,158,117,0.12)', fg: C.green },
  refer: { label: 'REFERS', bg: 'rgba(232,160,32,0.15)', fg: C.amber },
  refuse: { label: 'REFUSES', bg: 'rgba(226,75,74,0.12)', fg: C.red },
  unconfigured: { label: 'UNMEASURED', bg: 'rgba(136,150,168,0.14)', fg: C.slate },
  override: { label: 'OVERRIDDEN', bg: 'rgba(108,75,181,0.12)', fg: '#6C4BB5' },
};
const fmtM = (v) => v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;

export default function CapacityGatePage() {
  const api = useApi();
  const { user } = useAuth();
  const isPrincipal = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ per_postcode: '', flood: '', fire: '', cyclone: '', basis: '', source_document: '' });
  const [busy, setBusy] = useState(false);
  const [ovForm, setOvForm] = useState({ dimension_key: '', reason: '', hours: 24 });

  const load = useCallback(() => {
    api.get('/capacity/status').then(r => {
      setData(r.data);
      const l = r.data.limits || {};
      if (l.configured) setForm(f => ({
        ...f,
        per_postcode: String(l.per_postcode || ''),
        flood: String(l.per_cat_zone?.flood || ''), fire: String(l.per_cat_zone?.fire || ''),
        cyclone: String(l.per_cat_zone?.cyclone || ''),
        basis: l.basis || '', source_document: l.source_document || '',
      }));
    }).catch(() => toast.error('Could not load the capacity position'));
  }, [api]);

  useEffect(load, [load]);

  const saveLimits = async () => {
    setBusy(true);
    try {
      const r = await api.post('/capacity/limits', {
        per_postcode: Number(form.per_postcode) || 0,
        per_cat_zone: { flood: Number(form.flood) || 0, fire: Number(form.fire) || 0, cyclone: Number(form.cyclone) || 0 },
        basis: form.basis, source_document: form.source_document,
      });
      const over = r.data?.baseline?.over?.length || 0;
      toast.success('Aggregate limits recorded — the gate is now enforcing', {
        description: over > 0
          ? `${over} aggregate${over === 1 ? '' : 's'} already over the line at the moment the limit was entered. Those refer to an underwriter with the figure attached rather than being refused.`
          : 'Nothing on the book is over the line today.',
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the limits'); }
    setBusy(false);
  };

  const addOverride = async () => {
    setBusy(true);
    try {
      await api.post('/capacity/overrides', ovForm);
      toast.success('Override recorded and audited', { description: 'It expires on its own — nothing to remember to turn off.' });
      setOvForm({ dimension_key: '', reason: '', hours: 24 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Override refused'); }
    setBusy(false);
  };

  const revoke = async (id) => {
    try { await api.post(`/capacity/overrides/${id}/revoke`); toast.success('Override revoked'); load(); }
    catch (e) { toast.error('Could not revoke'); }
  };

  if (!data) return <div className="space-y-3"><Skeleton className="h-28 rounded-sm" /><Skeleton className="h-64 rounded-sm" /></div>;

  const t = data.thresholds;
  const enforcing = data.enforcing;

  return (
    <div className="space-y-4" data-testid="capacity-gate-page">
      <Card className="p-4 rounded-sm bg-white" data-testid="capacity-headline"
        style={{ border: `1.5px solid ${enforcing ? 'rgba(29,158,117,0.4)' : 'rgba(232,160,32,0.45)'}` }}>
        <div className="flex items-start gap-3">
          {enforcing
            ? <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" style={{ color: C.green }} />
            : <ShieldOff className="w-5 h-5 mt-0.5 shrink-0" style={{ color: C.amber }} />}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-medium" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>
                Treaty capacity gate
              </h2>
              <span className="text-[9px] px-2 py-0.5 rounded-sm font-bold tracking-wider"
                style={enforcing ? { background: 'rgba(29,158,117,0.12)', color: C.green } : { background: 'rgba(232,160,32,0.15)', color: C.amber }}
                data-testid="capacity-enforcing-chip">
                {enforcing ? 'ENFORCING' : 'NOT ENFORCING'}
              </span>
              <span className="text-[10px] font-mono" style={{ color: C.slate }}>
                refer {t.refer_pct}% · refuse {t.refuse_pct}% · PML at {t.pml_damage_ratio * 100}%
              </span>
            </div>
            <p className="text-sm mt-1.5 leading-relaxed max-w-4xl" style={{ color: C.navy }}>{data.headline}</p>
            <p className="text-[11px] mt-2 leading-relaxed max-w-4xl" style={{ color: C.slate }}>{data.single_event_cover_note}</p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="capacity-limits-form">
          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: C.blue }}>Aggregate limits — off the treaty slip</p>
          <p className="text-[11px] mt-1 mb-3 leading-relaxed" style={{ color: C.slate }}>
            The figure a single accumulation may reach, per postcode and per catastrophe zone. Not
            the XoL layer limit — that is cover for one loss. Leave a zone at zero and the gate
            reports it without judging it.
          </p>
          {[['per_postcode', 'Per postcode'], ['flood', 'Flood zone (high + extreme)'],
            ['fire', 'Fire zone (high + extreme)'], ['cyclone', 'Cyclone zone (high + extreme)']].map(([k, label]) => (
            <div key={k} className="flex items-center gap-2 mb-2">
              <label className="text-[11px] w-52 shrink-0" style={{ color: C.navy }} htmlFor={`cap-${k}`}>{label}</label>
              <input id={`cap-${k}`} type="number" min="0" value={form[k]} disabled={!isPrincipal}
                onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`capacity-input-${k}`}
                placeholder="0" className="flex-1 text-xs p-1.5 rounded-sm border font-mono disabled:bg-[#F5F7FA]"
                style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
              <span className="text-[10px] w-16 text-right" style={{ color: C.slate }}>
                {Number(form[k]) > 0 ? fmtM(Number(form[k])) : '—'}
              </span>
            </div>
          ))}
          <div className="mt-3">
            <label className="text-[11px]" style={{ color: C.navy }} htmlFor="cap-basis">Basis — which clause or slip these come from</label>
            <input id="cap-basis" value={form.basis} disabled={!isPrincipal} data-testid="capacity-input-basis"
              onChange={e => setForm({ ...form, basis: e.target.value })}
              placeholder="e.g. Treaty slip 2026, clause 7 — aggregate per accumulation zone"
              className="w-full text-xs p-1.5 mt-1 rounded-sm border disabled:bg-[#F5F7FA]"
              style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
            <p className="text-[10px] mt-1" style={{ color: form.basis.trim().length >= 10 ? C.slate : C.amber }}>
              {form.basis.trim().length >= 10 ? 'Recorded with the limit and audited' : 'At least 10 characters — a limit with no provenance is a number somebody typed'}
            </p>
          </div>
          <input value={form.source_document} disabled={!isPrincipal} data-testid="capacity-input-source"
            onChange={e => setForm({ ...form, source_document: e.target.value })}
            placeholder="Source document (optional)" aria-label="Source document"
            className="w-full text-xs p-1.5 mt-2 rounded-sm border disabled:bg-[#F5F7FA]"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
          {isPrincipal ? (
            <button onClick={saveLimits} disabled={busy || form.basis.trim().length < 10} data-testid="capacity-save-limits"
              className="mt-3 w-full py-2 rounded-sm text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: C.blue }}>
              {enforcing ? 'Update the limits and re-baseline' : 'Record the limits — the gate starts holding binds'}
            </button>
          ) : (
            <p className="text-[11px] mt-3" style={{ color: C.slate }}>Only the platform owner or an MGA admin can set a treaty limit.</p>
          )}
          {data.limits?.set_by && (
            <p className="text-[10px] mt-2 font-mono" style={{ color: C.slate }}>
              Set by {data.limits.set_by} · {String(data.limits.set_at).slice(0, 16).replace('T', ' ')}
            </p>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="capacity-zones">
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: C.blue }}>Catastrophe zones</p>
            <table className="w-full text-[11px]">
              <thead><tr style={{ color: C.slate }}>
                {['Zone', 'Policies', 'Retained TIV', 'PML', 'Limit', 'Util', ''].map(h => (
                  <th key={h} className="text-left pb-1 text-[9px] uppercase tracking-wider font-medium">{h}</th>))}
              </tr></thead>
              <tbody>
                {data.cat_zones.map(z => {
                  const v = VERDICT[z.verdict] || VERDICT.unconfigured;
                  return (
                    <tr key={z.zone} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`capacity-zone-${z.zone}`}>
                      <td className="py-1.5 capitalize font-medium" style={{ color: C.navy }}>{z.zone}</td>
                      <td className="py-1.5" style={{ color: C.navy }}>{z.policies}</td>
                      <td className="py-1.5" style={{ color: C.blue }}>{fmtM(z.retained_tiv)}</td>
                      <td className="py-1.5" style={{ color: C.slate }}>{fmtM(z.pml)}</td>
                      <td className="py-1.5 font-mono" style={{ color: z.limit > 0 ? C.navy : C.slate }}>{z.limit > 0 ? fmtM(z.limit) : 'none'}</td>
                      <td className="py-1.5 font-semibold" style={{ color: v.fg }}>{z.utilisation_pct == null ? '—' : `${z.utilisation_pct}%`}</td>
                      <td className="py-1.5">
                        <span className="px-1.5 py-0.5 rounded-sm text-[8px] font-bold" style={{ background: v.bg, color: v.fg }}>{v.label}</span>
                        {z.grandfathered && <span className="ml-1 text-[8px]" style={{ color: C.amber }}>pre-existing</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {data.baseline && (
            <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(232,160,32,0.3)' }} data-testid="capacity-baseline">
              <div className="flex items-center gap-2">
                <History className="w-3.5 h-3.5" style={{ color: C.amber }} />
                <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: C.amber }}>
                  Already over the line when the limit was entered
                </p>
              </div>
              <p className="text-[11px] mt-1 leading-relaxed" style={{ color: C.slate }}>{data.baseline.note}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.baseline.over.length === 0
                  ? <span className="text-[11px]" style={{ color: C.green }}>Nothing — the whole book was inside the limit.</span>
                  : data.baseline.over.map(o => (
                    <span key={o.key} className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono" data-testid={`capacity-grandfathered-${o.key}`}
                      style={{ background: 'rgba(232,160,32,0.12)', color: C.amber }}>
                      {o.key} · {o.utilisation_pct}%
                    </span>
                  ))}
              </div>
              <p className="text-[10px] mt-2 font-mono" style={{ color: C.slate }}>
                Taken {String(data.baseline.taken_at).slice(0, 16).replace('T', ' ')} by {data.baseline.taken_by} · {data.baseline.dimensions_checked} aggregates checked
              </p>
            </Card>
          )}
        </div>
      </div>

      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="capacity-postcodes">
        <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: C.amber }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            Largest accumulations — what the gate would say to the next risk
          </h3>
          <span className="ml-auto text-[10px]" style={{ color: C.slate }}>{data.postcodes_measured} postcodes on the live book</span>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
            {['Postcode', 'Policies', 'Retained TIV', 'PML', 'Util of limit', '× event cover', 'Verdict'].map(h => (
              <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {data.postcodes.map(p => {
              const v = VERDICT[p.verdict] || VERDICT.unconfigured;
              return (
                <tr key={p.postcode} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`capacity-pc-${p.postcode}`}>
                  <td className="py-2 px-3 font-mono font-semibold" style={{ color: C.navy }}>{p.postcode} {p.state}</td>
                  <td className="py-2 px-3" style={{ color: C.navy }}>{p.policies}</td>
                  <td className="py-2 px-3 font-semibold" style={{ color: C.blue }}>{fmtM(p.retained_tiv)}</td>
                  <td className="py-2 px-3" style={{ color: C.slate }}>{fmtM(p.pml)}</td>
                  <td className="py-2 px-3 font-semibold" style={{ color: v.fg }}>{p.utilisation_pct == null ? '—' : `${p.utilisation_pct}%`}</td>
                  <td className="py-2 px-3" style={{ color: C.slate }}>{p.pml_multiple_of_event_cover == null ? '—' : `${p.pml_multiple_of_event_cover}×`}</td>
                  <td className="py-2 px-3">
                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold" style={{ background: v.bg, color: v.fg }}>{v.label}</span>
                    {p.grandfathered && <span className="ml-1 text-[9px]" style={{ color: C.amber }}>pre-existing</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="capacity-overrides">
        <div className="flex items-center gap-2 mb-2">
          <Unlock className="w-3.5 h-3.5" style={{ color: '#6C4BB5' }} />
          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#6C4BB5' }}>Overrides</p>
          <span className="text-[11px]" style={{ color: C.slate }}>
            An override of a treaty limit is a position taken in a named person's own name. Time-boxed, audited, revocable.
          </span>
        </div>
        {isPrincipal && (
          <div className="flex flex-wrap items-end gap-2 mb-3 p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
            <div className="flex-1 min-w-[150px]">
              <label className="text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }} htmlFor="ov-dim">Aggregate</label>
              <select id="ov-dim" value={ovForm.dimension_key} onChange={e => setOvForm({ ...ovForm, dimension_key: e.target.value })}
                data-testid="capacity-override-dimension"
                className="w-full text-xs p-1.5 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
                <option value="">Choose one…</option>
                {data.postcodes.map(p => <option key={p.dimension_key} value={p.dimension_key}>{`postcode ${p.postcode}`}</option>)}
                {data.cat_zones.map(z => <option key={z.dimension_key} value={z.dimension_key}>{`${z.zone} zone`}</option>)}
              </select>
            </div>
            <div className="flex-[2] min-w-[220px]">
              <label className="text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }} htmlFor="ov-reason">Reason (10+ characters)</label>
              <input id="ov-reason" value={ovForm.reason} onChange={e => setOvForm({ ...ovForm, reason: e.target.value })}
                data-testid="capacity-override-reason" placeholder="e.g. Facultative placement bound — slip attached"
                className="w-full text-xs p-1.5 rounded-sm border" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
            </div>
            <div className="w-24">
              <label className="text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }} htmlFor="ov-hours">Hours</label>
              <input id="ov-hours" type="number" min="1" max="168" value={ovForm.hours} data-testid="capacity-override-hours"
                onChange={e => setOvForm({ ...ovForm, hours: Number(e.target.value) })}
                className="w-full text-xs p-1.5 rounded-sm border font-mono" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
            </div>
            <button onClick={addOverride} data-testid="capacity-override-add"
              disabled={busy || !ovForm.dimension_key || ovForm.reason.trim().length < 10}
              className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-40" style={{ background: '#6C4BB5' }}>
              Record override
            </button>
          </div>
        )}
        {data.overrides.length === 0 ? (
          <p className="text-[11px] flex items-center gap-1.5" style={{ color: C.slate }}>
            <Lock className="w-3 h-3" /> None live. Every refusal stands.
          </p>
        ) : data.overrides.map(o => (
          <div key={o.override_id} className="flex flex-wrap items-center gap-2 py-1.5 border-t text-[11px]"
            style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`capacity-override-${o.override_id}`}>
            <span className="font-mono font-semibold" style={{ color: C.navy }}>{o.dimension_key}</span>
            <span style={{ color: C.slate }}>{o.reason}</span>
            <span className="font-mono text-[10px]" style={{ color: C.slate }}>
              {o.officer} · expires {String(o.expires_at).slice(0, 16).replace('T', ' ')}
            </span>
            {isPrincipal && (
              <button onClick={() => revoke(o.override_id)} data-testid={`capacity-override-revoke-${o.override_id}`}
                className="ml-auto text-[10px] font-semibold" style={{ color: C.red }}>Revoke</button>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
